import $ from '../core/Dom';
import Viewport from '../core/Viewport';
import Dispatch from '../core/Dispatch';
import EcoMode from '../lib/EcoMode';

import { USER_PREFERENCES_UPDATED } from '../lib/events';
import { loadGsap } from '../lib/async-bundles';

export default (el, props) => {
    const STICKY_SHOW_THRESHOLD = 128;
    
    const $el = $(el);
    const { openText, closeText } = props;
    
    const $body = $('body');
    const $panelBg = $el.find('[data-menu-panel-backdrop]');
    const $menuMobile = $el.find('[data-menu-mobile]');
    const $stickyInfoBar = $el.find('[data-sticky-info-bar]');
    const $expandedHeaderMenu = $el.find('[data-expanded-header-menu]');

    let gsap = null;

    let $activeMenuPanel = null;
    let isAnimating = false;
    let mobileMenuOpen = 0;
    let isDesktopMenu = Viewport.breakpoint.size >= 1200;
    let hasStickyBar = false;
    let stickyBarVisible = false;
    let tallestMenuPanelHeight = 0;

    const init = () => {
        if (EcoMode.shouldAnimate()) {
            loadBundles();
        }

        $el.on('click', '[data-panel-trigger]', e => {
            e.preventDefault();

            if (isAnimating) return;

            const panelId = $(e.triggerTarget).attr('data-panel-trigger');
            showMenuPanel(panelId);
        });

        $el.on('click', '[data-menu-panel-close]', e => {
            e.preventDefault();

            if (isAnimating) return;

            closeActivePanel();
        });

        $el.on('click', '[data-menu-toggle]', e => {
            e.preventDefault();

            if (isAnimating) return;

            toggleMobileMenuPanel();
        });

        $panelBg.on('click', e => {
            if (isAnimating) return;

            if ($activeMenuPanel !== null) {
                closeActivePanel();
            }
        });

        $body.on('keyup', onBodyKeyUp);

        hasStickyBar = $stickyInfoBar.find('*').length > 0;

        getTallestMenuPanelHeight();

        Viewport.on('resize', onResize);
        Viewport.on('scroll', onScroll);
        Dispatch.on(USER_PREFERENCES_UPDATED, onUserPreferencesUpdated);
    };

    const destroy = () => {
        $el.off('click');
        $panelBg.off('click');
        $body.off('keyup');

        Viewport.off('resize', onResize);
        Viewport.off('scroll', onScroll);
        Dispatch.off(USER_PREFERENCES_UPDATED, onUserPreferencesUpdated);
    };

    const onResize = e => {
        const newIsDesktop = Viewport.breakpoint.size >= 1200;

        // TODO: Make adjustments when going from mobile to desktop or vice versa

        isDesktopMenu = newIsDesktop;

        if (isDesktopMenu) {
            getTallestMenuPanelHeight();
            if ($activeMenuPanel !== null) {
                $activeMenuPanel.css({ height: tallestMenuPanelHeight });
            }
        }
    };
    
    const onUserPreferencesUpdated = () => {
        if (EcoMode.shouldAnimate()) {
            if (gsap === null) {
                loadBundles();
            }
        } else {
            gsap = null;
        }
    };
    
    const loadBundles = () => {
        loadGsap(({ default: loadedData }) => {
            gsap = loadedData;
            // gsap.defaults({ overwrite: 'auto' });
        });
    };

    const getTallestMenuPanelHeight = () => {
        tallestMenuPanelHeight = 0;

        $el.find('[data-menu-panel]').each(panel => {
            const $panel = $(panel);
            const displayVal = $panel.css('display');

            $panel.css({ display: 'block', height: '' });
            tallestMenuPanelHeight = Math.max(tallestMenuPanelHeight, $panel.height());
            $panel.css({ display: displayVal });
        });

        tallestMenuPanelHeight += 1; // +1 fixes rounding bug
    };

    const onScroll = () => {
        if (hasStickyBar) {
            if (stickyBarVisible && Viewport.scrollTop < STICKY_SHOW_THRESHOLD) {
                $stickyInfoBar.removeClass('is-visible');
                stickyBarVisible = false;
            } else if (!stickyBarVisible && Viewport.scrollTop > STICKY_SHOW_THRESHOLD) {
                $stickyInfoBar.addClass('is-visible');
                stickyBarVisible = true;

            }
        }
    };

    const onBodyKeyUp = e => {
        const key = e.key || e.keyCode || e.which || null;

        if (['Escape', 27].indexOf(key) > -1) {
            if (mobileMenuOpen) {
                toggleMobileMenuPanel();
            }
            if ($activeMenuPanel !== null) {
                closeActivePanel();
            }
        }
    };

    const collapseHeader = () => {
        $body.addClass('is-animated-header');

        setTimeout(() => {
            $body.addClass('collapse-header');
        }, 10);
    };

    const uncollapseHeader = () => {
        $body.removeClass('collapse-header');

        setTimeout(() => {
            $body.removeClass('is-animated-header');
        }, 400);
    };

    const toggleMobileMenuPanel = () => {
        if (mobileMenuOpen) {

            if (gsap) {
                isAnimating = true;

                gsap.to($menuMobile.get(0), {
                    duration: 0.1,
                    opacity: 0,
                    delay: $activeMenuPanel !== null ? 0.4 : 0,
                    ease: 'sine.in',
                    onComplete: () => {
                        $menuMobile.css({ display: '', opacity: 1, y: '' });
                        uncollapseHeader();
                        isAnimating = false;
                    }
                });
            } else {
                $menuMobile.css({ display: '' });
                uncollapseHeader();
            }
            
            $panelBg.addClass('pointer-events-none').css({ opacity: 0 });

        } else {
            const isExpanded = window.getComputedStyle(document.body).getPropertyValue('--expanded-header-size') > 0;
            const baseDelay = isExpanded ? 0.4 : 0;

            $menuMobile.css({ display: 'block' });

            if (gsap) {
                gsap.set($menuMobile.get(0), { y: '-100%', opacity: 0 });
                gsap.to($menuMobile.get(0), {
                    duration: 0.5,
                    delay: baseDelay,
                    y: 0,
                    ease: 'quint.out',
                    onStart: () => {
                        $menuMobile.css({ opacity: 1 });
                    }
                });
            }

            $panelBg.removeClass('pointer-events-none').css({ opacity: 1 });
            collapseHeader();
        }

        mobileMenuOpen = !mobileMenuOpen;
        
        if (mobileMenuOpen) {
            $body.addClass('is-mobilemenu-open');
        } else {
            $body.removeClass('is-mobilemenu-open');
        }
        
        const $toggle = $el.find('[data-menu-toggle]');
        $toggle.text(mobileMenuOpen ? closeText : openText);
        //$toggle.focus();
    };

    const showMenuPanel = panelId => {
        const $panel = $el.find(`#${panelId}`);
        const $panelInner = $panel.find('[data-menu-panel-inner]');
        const isSame = $activeMenuPanel !== null && $activeMenuPanel.attr('id') === panelId;
        const willOpen = !isSame && $panel.length > 0;
        let activePanelHeight = 0;

        if ($activeMenuPanel !== null) {
            activePanelHeight = $activeMenuPanel.height();
            closeActivePanel(willOpen);
        }

        if (willOpen) {
            $expandedHeaderMenu.addClass('has-open-panel');
            $expandedHeaderMenu.find('[data-panel-trigger="' + panelId + '"]').addClass('is-active');

            $panel.css({ display: 'block' });

            if (gsap) {
                const isExpanded = window.getComputedStyle(document.body).getPropertyValue('--expanded-header-size') > 0;
                const baseDelay = isExpanded ? 0.4 : 0;
                isAnimating = true;

                if (isDesktopMenu) {

                    gsap.set($panel.get(0), { height: activePanelHeight, overflow: 'hidden' });
                    gsap.set($panelInner.get(0), { opacity: 0 });

                    gsap.to($panel.get(0), {
                        duration: 0.5,
                        delay: baseDelay,
                        height: tallestMenuPanelHeight,
                        ease: 'quint.inOut',
                        onComplete: () => {
                            gsap.set($panel.get(0), { overflow: '' });
                        }
                    });
                    gsap.to($panelInner.get(0), {
                        duration: 0.6,
                        delay: baseDelay + 0.3,
                        opacity: 1,
                        ease: 'sine.out',
                        onComplete: () => {
                            isAnimating = false;
                        }
                    });

                } else {
                    gsap.set($panel.get(0), { x: '100%', opacity: 1 });
                    gsap.to($panel.get(0), {
                        x: '0%',
                        duration: 0.5,
                        ease: 'quint.out',
                        onComplete: () => {
                            isAnimating = false;
                        }
                    });

                }
            }

            $activeMenuPanel = $panel;
            $panelBg.removeClass('pointer-events-none').css({ opacity: 1 });
            collapseHeader();
        }

    };

    const closeActivePanel = isOpeningNew => {
        if ($activeMenuPanel === null) {
            return;
        }

        const $panel = $activeMenuPanel;
        const $panelInner = $panel.find('[data-menu-panel-inner]');

        $expandedHeaderMenu.removeClass('has-open-panel');  
        $expandedHeaderMenu.find('[data-panel-trigger]').removeClass('is-active');

        if (gsap && !isOpeningNew) {
            isAnimating = true;

            if (isDesktopMenu) {
                gsap.set($panel.get(0), { overflow: 'hidden' });

                gsap.to($panelInner.get(0), {
                    duration: 0.1,
                    opacity: 0,
                    ease: 'sine.out'
                });
                gsap.to($panel.get(0), {
                    duration: 0.2,
                    height: 0,
                    ease: 'sine.in',
                    onComplete: () => {
                        isAnimating = false;
                        $panel.css({ display: '', height: '' });
                        gsap.set($panel.get(0), { display: '', height: '', overflow: '' });
                    }
                });

            } else {
                gsap.to($panel.get(0), {
                    x: '100%',
                    duration: 0.3,
                    ease: 'sine.in',
                    onComplete: () => {
                        isAnimating = false;
                        gsap.set($panel.get(0), { x: '', display: '', opacity: 1 });
                    }
                });

            }
        } else {
            $activeMenuPanel.css({ display: '' });
        }

        if (!isOpeningNew) {
            $panelBg.addClass('pointer-events-none').css({ opacity: 0 });

            if (isDesktopMenu) {
                uncollapseHeader();
            }
        }

        $activeMenuPanel = null;
    };

    return {
        init,
        destroy
    };
};
